import { getQueryTransformation } from '@/api/helpers';
import { FiltersType, FilterValue } from '@/api/types';
import { FilterNamesEnum, getFilters } from '@/client/helpers';
import { TableNamesEnum } from '@/common/constants';
import { useQueryParams } from '@/hooks/query.hook';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import {
  selectTablePageSize,
  setTablePageSize,
} from '@/store/features/filters';
import { DataTablePageEvent } from 'primereact/datatable';
import { useEffect, useState } from 'react';

type TableHookOptions = {
  defaultSort?: [string, 'asc' | 'desc'];
  tableName?: TableNamesEnum;
};

export const useTable = ({
  defaultSort = ['created', 'desc'],
  tableName,
}: TableHookOptions = {}) => {
  const { params, setSearchParams } = useQueryParams(
    getQueryTransformation<{ name: string }>(),
  );
  const dispatch = useAppDispatch();
  const pageSizes = useAppSelector(selectTablePageSize);

  const persistedTake = pageSizes ? pageSizes[tableName ?? ''] ?? 10 : 10;

  const [skip, setSkip] = useState<number>(
    params.skip !== undefined ? Number(params.skip) : 0,
  );
  const [take, setTake] = useState<number>(
    params.take !== undefined ? Number(params.take) : 10,
  );

  useEffect(() => {
    if (params.take === undefined) {
      setTake(persistedTake);
    }
  }, [persistedTake]);
  const [apiFilters, setFilters] = useState<FiltersType>(
    params.filters ? getFilters(params.filters) : [],
  );
  const [filterValues, setFilterValues] = useState<Record<string, any>>(
    params.filters ?? {},
  );
  const [sort, setSort] = useState<[string, 'asc' | 'desc'] | null>(
    params['sort[]'] ?? defaultSort,
  );

  const updateUrl = (updates: {
    skip?: number;
    take?: number;
    filters?: Record<string, any>;
    sort?: [string, 'asc' | 'desc'] | null;
  }) => {
    setSearchParams({
      filters: updates.filters ?? filterValues,
      'sort[]': updates.sort ?? sort,
      skip: updates.skip ?? skip,
      take: updates.take ?? take,
    });
  };

  const onPage = (event: DataTablePageEvent) => {
    setSkip(event.first);
    setTake(event.rows);
    updateUrl({ skip: event.first, take: event.rows });
    dispatch(setTablePageSize({ [tableName ?? '']: event.rows }));
  };

  const onGridRowChange = (value: number) => {
    setTake(value);
    updateUrl({ take: value });
    dispatch(setTablePageSize({ [tableName ?? '']: value }));
  };

  const onGridPageChange = (value: number) => {
    setSkip(value);
    updateUrl({ skip: value });
  };

  const onSort = (event: DataTablePageEvent) => {
    const newSort: [string, 'asc' | 'desc'] = [
      event.sortField,
      event.sortOrder === -1 ? 'desc' : 'asc',
    ];
    setSkip(0);
    setSort(newSort);
    updateUrl({ sort: newSort, skip: 0 });
  };

  const onFilter = (values: Record<FilterNamesEnum, FilterValue>) => {
    if (JSON.stringify(values) === JSON.stringify(filterValues)) {
      return;
    }
    setSkip(0);
    setFilterValues(values);
    setFilters(getFilters(values));
    updateUrl({ filters: values, skip: 0 });
  };

  return {
    skip,
    take,
    sort,
    apiFilters,
    onPage,
    onSort,
    setSkip,
    onFilter,
    onGridPageChange,
    onGridRowChange,
  };
};
